import { devLog, devError } from '../utils/devLogger';
// Production Environment Monitoring Service
// Handles error tracking, performance monitoring, and system health checks

import { auth, db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, limit, getDocs } from 'firebase/firestore';

class MonitoringService {
  constructor() {
    this.isProduction = process.env.REACT_APP_ENV === 'production';
    this.errorQueue = [];
    this.performanceMetrics = [];
    this.healthChecks = new Map();
    this.startTime = Date.now();

    // Initialize monitoring
    this.initializeErrorHandling();
    this.initializePerformanceMonitoring();
    this.startHealthChecks();
  }

  // Error Tracking
  initializeErrorHandling() {
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'javascript-error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'unhandled-promise-rejection',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    });

    this.setupReactErrorBoundary();
  }

  setupReactErrorBoundary() {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('React')) {
        this.logError({
          type: 'react-error',
          message: args.join(' '),
          stack: new Error().stack,
          userAgent: navigator.userAgent,
          url: window.location.href
        });
      }
      originalConsoleError.apply(console, args);
    };
  }

  async logError(errorData) {
    const enrichedError = {
      ...errorData,
      timestamp: new Date().toISOString(),
      userId: auth.currentUser?.uid || 'anonymous',
      userEmail: auth.currentUser?.email || 'unknown',
      sessionId: this.getSessionId(),
      buildVersion: process.env.REACT_APP_VERSION || 'unknown',
      environment: process.env.REACT_APP_ENV || 'development'
    };

    this.errorQueue.push(enrichedError);

    if (!this.isProduction) {
      devError('🚨 Error tracked:', enrichedError);
    }

    if (this.errorQueue.length >= 10) {
      await this.flushErrorQueue();
    }
  }

  async flushErrorQueue() {
    if (this.errorQueue.length === 0) return;

    try {
      const errorsToFlush = [...this.errorQueue];
      this.errorQueue = [];

      if (this.isProduction) {
        const batch = errorsToFlush.map(error =>
          addDoc(collection(db, 'system_errors'), {
            ...error,
            createdAt: serverTimestamp()
          })
        );
        await Promise.all(batch);
      } else {
        devLog('📊 Would flush errors to database:', errorsToFlush);
      }
    } catch (error) {
      devError('Failed to flush error queue:', error);
      this.errorQueue.unshift(...this.errorQueue);
    }
  }

  // Performance Monitoring
  initializePerformanceMonitoring() {
    window.addEventListener('load', () => {
      this.trackPageLoad();
    });

    this.setupNavigationMonitoring();
    this.setupResourceMonitoring();
  }

  trackPageLoad() {
    if (typeof window.performance !== 'undefined') {
      const timing = window.performance.timing;
      const navigation = window.performance.navigation;

      const metrics = {
        type: 'page-load',
        timestamp: new Date().toISOString(),
        url: window.location.href,
        loadTime: timing.loadEventEnd - timing.navigationStart,
        domContentLoadedTime: timing.domContentLoadedEventEnd - timing.navigationStart,
        firstPaintTime: this.getFirstPaintTime(),
        navigationType: this.getNavigationType(navigation.type),
        redirectCount: navigation.redirectCount
      };

      this.recordPerformanceMetric(metrics);
    }
  }

  getFirstPaintTime() {
    if (typeof window.performance.getEntriesByType === 'function') {
      const paintEntries = window.performance.getEntriesByType('paint');
      const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
      return firstPaint ? firstPaint.startTime : null;
    }
    return null;
  }

  getNavigationType(type) {
    switch (type) {
      case 0: return 'navigate';
      case 1: return 'reload';
      case 2: return 'back-forward';
      default: return 'unknown';
    }
  }

  setupNavigationMonitoring() {
    let lastUrl = window.location.href;
    const observer = new MutationObserver(() => {
      if (lastUrl !== window.location.href) {
        this.recordPerformanceMetric({
          type: 'navigation',
          timestamp: new Date().toISOString(),
          fromUrl: lastUrl,
          toUrl: window.location.href
        });
        lastUrl = window.location.href;
      }
    });

    observer.observe(document, { childList: true, subtree: true });
  }

  setupResourceMonitoring() {
    if (typeof window.performance.getEntriesByType === 'function') {
      const resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration > 1000) {
            this.recordPerformanceMetric({
              type: 'slow-resource',
              timestamp: new Date().toISOString(),
              resourceName: entry.name,
              resourceType: entry.initiatorType,
              duration: entry.duration,
              size: entry.transferSize || 0
            });
          }
        });
      });

      resourceObserver.observe({ entryTypes: ['resource'] });
    }
  }

  recordPerformanceMetric(metric) {
    const enrichedMetric = {
      ...metric,
      userId: auth.currentUser?.uid || 'anonymous',
      sessionId: this.getSessionId(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    this.performanceMetrics.push(enrichedMetric);

    if (!this.isProduction) {
      devLog('📈 Performance metric:', enrichedMetric);
    }

    if (this.performanceMetrics.length >= 20) {
      this.flushPerformanceMetrics();
    }
  }

  async flushPerformanceMetrics() {
    if (this.performanceMetrics.length === 0) return;

    try {
      const metricsToFlush = [...this.performanceMetrics];
      this.performanceMetrics = [];

      if (this.isProduction) {
        const batch = metricsToFlush.map(metric =>
          addDoc(collection(db, 'performance_metrics'), {
            ...metric,
            createdAt: serverTimestamp()
          })
        );
        await Promise.all(batch);
      } else {
        devLog('📊 Would flush performance metrics:', metricsToFlush);
      }
    } catch (error) {
      devError('Failed to flush performance metrics:', error);
    }
  }

  // System Health Checks
  startHealthChecks() {
    setInterval(() => {
      this.performHealthCheck();
    }, 60000);

    this.performHealthCheck();
  }

  async performHealthCheck() {
    const healthData = {
      timestamp: new Date().toISOString(),
      uptime: Date.now() - this.startTime,
      memory: this.getMemoryUsage(),
      network: await this.checkNetworkStatus(),
      firebase: await this.checkFirebaseConnection(),
      integrations: await this.checkIntegrations()
    };

    this.healthChecks.set(Date.now(), healthData);

    if (this.healthChecks.size > 100) {
      const oldestKey = Math.min(...this.healthChecks.keys());
      this.healthChecks.delete(oldestKey);
    }

    this.checkForCriticalIssues(healthData);

    if (!this.isProduction) {
      devLog('💓 Health check:', healthData);
    }
  }

  getMemoryUsage() {
    if (typeof window.performance.memory !== 'undefined') {
      return {
        used: window.performance.memory.usedJSHeapSize,
        total: window.performance.memory.totalJSHeapSize,
        limit: window.performance.memory.jsHeapSizeLimit,
        percentage: (window.performance.memory.usedJSHeapSize / window.performance.memory.jsHeapSizeLimit) * 100
      };
    }
    return null;
  }

  async checkNetworkStatus() {
    return {
      online: navigator.onLine,
      effectiveType: navigator.connection?.effectiveType || 'unknown',
      downlink: navigator.connection?.downlink || null,
      rtt: navigator.connection?.rtt || null
    };
  }

  async checkFirebaseConnection() {
    try {
      const startTime = Date.now();
      await getDocs(query(collection(db, 'system_health'), limit(1)));
      return {
        status: 'connected',
        responseTime: Date.now() - startTime
      };
    } catch (error) {
      return {
        status: 'error',
        error: error.message
      };
    }
  }

  async checkIntegrations() {
    const integrations = {
      googleMeet: 'unknown',
      classroom: 'unknown',
      googleForms: 'unknown',
      docusign: 'unknown'
    };

    try {
      const googleMeetService = await import('../services/googleMeetService');
      integrations.googleMeet = googleMeetService.default ? 'available' : 'unavailable';
    } catch {
      integrations.googleMeet = 'error';
    }

    try {
      const classroomService = await import('../services/googleClassroomIntegration');
      integrations.classroom = classroomService.default ? 'available' : 'unavailable';
    } catch {
      integrations.classroom = 'error';
    }

    try {
      const googleFormsService = await import('../services/googleFormsService');
      integrations.googleForms = googleFormsService.default ? 'available' : 'unavailable';
    } catch {
      integrations.googleForms = 'error';
    }

    try {
      const docusignService = await import('../services/eSignature');
      integrations.docusign = docusignService.default ? 'available' : 'unavailable';
    } catch {
      integrations.docusign = 'error';
    }

    return integrations;
  }

  checkForCriticalIssues(healthData) {
    const issues = [];

    if (healthData.memory && healthData.memory.percentage > 90) {
      issues.push('High memory usage detected');
    }

    if (!healthData.network.online) {
      issues.push('Network connectivity lost');
    }

    if (healthData.firebase.status === 'error') {
      issues.push('Firebase connection failed');
    }

    if (issues.length > 0) {
      this.logError({
        type: 'critical-health-issue',
        message: `Critical health issues detected: ${issues.join(', ')}`,
        healthData
      });
    }
  }

  // Utility Methods
  getSessionId() {
    let sessionId = sessionStorage.getItem('monitoring_session_id');
    if (!sessionId) {
      sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
      sessionStorage.setItem('monitoring_session_id', sessionId);
    }
    return sessionId;
  }

  trackEvent(eventName, eventData = {}) {
    this.recordPerformanceMetric({
      type: 'custom-event',
      eventName,
      eventData,
      timestamp: new Date().toISOString()
    });
  }

  trackUserAction(action, details = {}) {
    this.recordPerformanceMetric({
      type: 'user-action',
      action,
      details,
      timestamp: new Date().toISOString()
    });
  }

  getSystemStatus() {
    const latestHealthCheck = Array.from(this.healthChecks.values()).pop();
    const recentErrors = this.errorQueue.slice(-5);

    return {
      status: this.determineOverallStatus(latestHealthCheck, recentErrors),
      uptime: Date.now() - this.startTime,
      lastHealthCheck: latestHealthCheck,
      recentErrors,
      errorCount: this.errorQueue.length,
      metricsCount: this.performanceMetrics.length
    };
  }

  determineOverallStatus(healthCheck, errors) {
    if (!healthCheck) return 'unknown';

    if (errors.length > 5) return 'critical';
    if (healthCheck.firebase?.status === 'error') return 'degraded';
    if (healthCheck.memory?.percentage > 80) return 'warning';

    return 'healthy';
  }

  async shutdown() {
    await this.flushErrorQueue();
    await this.flushPerformanceMetrics();
  }
}

// Create singleton instance
const monitoringService = new MonitoringService();

window.addEventListener('beforeunload', () => {
  monitoringService.shutdown();
});

export default monitoringService;
